import React, { useState, useEffect } from 'react';
import { StyleSheet, View, Pressable } from 'react-native';
import { Image } from 'expo-image';
import { useTheme } from '@/context/ThemeContext';
import { useAuthStore } from '@/store';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import Button from '@/components/Buttons/Button';
import UserUpdateEmailForm from '@/components/Forms/UserUpdateEmailForm';
import UserUpdatePasswordForm from '@/components/Forms/UserUpdatePasswordForm';
import User2FAUpdateForm from '@/components/Forms/User2FAUpdateForm';
import ConnectedAccountsSettings from './ConnectedAccountsSettings';
import ProfileSettings from './ProfileSettings';
import { SettingsCardGroup } from './SettingsCards';
import { useModalHelper } from '@/hooks/useModalHelper';
import { useTranslation } from '@/hooks/useTranslation';

function AccountRow({ title, subtitle, status, icon, first, last, onPress }) {
  const { colors } = useTheme();
  const [focused, setFocused] = useState(false);
  return <Pressable accessibilityRole="button" accessibilityLabel={status ? `${title}, ${status}` : title}
    onPress={onPress} onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
    style={({ hovered, pressed }) => [styles.row, !first && { borderTopWidth: StyleSheet.hairlineWidth }, {
      borderTopColor: colors.border_color_secondary,
      backgroundColor: hovered || pressed || focused ? colors.solid_color_secondary : 'transparent',
      ...(first ? { borderTopLeftRadius: 15, borderTopRightRadius: 15 } : {}),
      ...(last ? { borderBottomLeftRadius: 15, borderBottomRightRadius: 15 } : {}),
    }]}>
    <View style={[styles.rowIcon, { backgroundColor: colors.solid_color_secondary }]}>
      <Icon name={icon} size={18} color={colors.text_color_secondary} />
    </View>
    <View style={styles.rowCopy}>
      <View style={styles.rowTitle}>
        <Text weight="medium" style={{ flexShrink: 1 }}>{title}</Text>
        {!!status && <Text size="xs" color="secondary" style={[styles.status, { backgroundColor: colors.solid_color_secondary }]}>{status}</Text>}
      </View>
      <Text size="s" color="secondary" style={styles.description}>{subtitle}</Text>
    </View>
    <Icon name="chevron-right" size={12} color={colors.text_color_tertiary} />
  </Pressable>;
}

const DETAIL_IDS = ['profile', 'email', 'password', '2fa', 'connected'];

function AccountSettings({ onSetHeaderActions, onDirtyChange, initialTab }) {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const user = useAuthStore(state => state.user);
  const hasPassword = user?.hasPassword;
  const [activeTab, setActiveTab] = useState(DETAIL_IDS.includes(initialTab) ? initialTab : 'overview');
  const [dirty, setDirty] = useState(false);
  const [avatarFailed, setAvatarFailed] = useState(false);
  const { useConfirmationModal } = useModalHelper();
  const confirm = useConfirmationModal();
  const copy = key => t(`settings.account.design.${key}`);
  useEffect(() => { onDirtyChange?.(dirty); }, [dirty, onDirtyChange]);
  useEffect(() => { setAvatarFailed(false); }, [user?.avatarPath]);

  const handleTabChange = tab => {
    if (tab === activeTab) return;
    const change = () => {
      setActiveTab(tab);
      setDirty(false);
      if (tab !== 'profile') onSetHeaderActions?.([]);
    };
    if (dirty) {
      confirm({ title: t('settingsPage.unsavedTitle'), message: t('settingsPage.unsavedMessage'), confirmText: t('settingsPage.discard'), onConfirm: change });
    } else change();
  };

  const titles = {
    profile: t('settingsWeb.profile'), email: t('settings.account.updateEmail'),
    password: hasPassword ? t('settings.account.changePassword') : t('settings.account.setPassword'),
    '2fa': copy('twoFactor'), connected: t('settings.account.connectedAccounts'),
  };
  const subtitles = {
    profile: copy('profileHelp'), email: t('settings.account.updateEmailSubtitle'),
    password: hasPassword ? t('settings.account.updatePassword') : t('settings.account.setPasswordSubtitle'),
    '2fa': t('settings.account.twoFactorSubtitle'), connected: t('settings.connectedAccounts.subtitle'),
  };

  if (activeTab !== 'overview') return <View testID="account-settings-detail" style={styles.detail}>
    <View style={styles.back}>
      <Button text={copy('back')} prependIcon="chevron-left" variant="transparent" size="large" onPress={() => handleTabChange('overview')} />
    </View>
    <SettingsCardGroup>
      <View style={styles.formCard}>
        {activeTab !== 'connected' && <View style={styles.detailHeading}>
          <Text size="xl" weight="semibold" accessibilityRole="header" accessibilityLevel={2}>{titles[activeTab]}</Text>
          <Text size="s" color="secondary" style={styles.description}>{subtitles[activeTab]}</Text>
        </View>}
        {activeTab === 'profile' && <ProfileSettings onSetHeaderActions={onSetHeaderActions} onDirtyChange={setDirty} />}
        {activeTab === 'email' && <UserUpdateEmailForm />}
        {activeTab === 'password' && <UserUpdatePasswordForm />}
        {activeTab === '2fa' && <User2FAUpdateForm />}
        {activeTab === 'connected' && <ConnectedAccountsSettings />}
      </View>
    </SettingsCardGroup>
  </View>;

  return <View testID="account-settings">
    <SettingsCardGroup>
      <View style={styles.identity}>
        <View style={[styles.avatar, { backgroundColor: colors.solid_color_secondary, borderColor: colors.border_color_secondary }]}>
          {user?.avatarPath && !avatarFailed
            ? <Image source={{ uri: user.avatarPath }} style={styles.avatarImage} contentFit="cover" onError={() => setAvatarFailed(true)} />
            : user?.fullName?.trim() ? <Text style={{ fontSize: 28 }} weight="medium">{user.fullName.trim()[0].toUpperCase()}</Text>
              : <Icon name="user" size={26} color={colors.text_color_secondary} />}
        </View>
        <View style={styles.identityCopy}>
          <Text size="xxl" weight="semibold" style={{ flexShrink: 1 }}>{user?.fullName?.trim() || copy('yourAccount')}</Text>
          {!!user?.email && <Text size="s" color="secondary" style={styles.email}>{user.email}</Text>}
          <View style={styles.editProfile}>
            <Button text={copy('editProfile')} variant="secondary" size="medium" onPress={() => handleTabChange('profile')} />
          </View>
        </View>
      </View>
    </SettingsCardGroup>

    <SettingsCardGroup title={copy('security')}>
      <AccountRow first title={t('settings.account.email')} subtitle={user?.email || copy('emailHelp')} icon="envelope"
        status={user?.email ? copy(user?.flags?.isEmailVerified ? 'verified' : 'notVerified') : undefined} onPress={() => handleTabChange('email')} />
      <AccountRow title={t('settings.account.password')} subtitle={hasPassword ? copy('passwordHelp') : t('settings.account.setPasswordSubtitle')}
        icon="lock" onPress={() => handleTabChange('password')} />
      <AccountRow last title={copy('twoFactor')} subtitle={copy('twoFactorHelp')} icon="shield-check"
        status={copy(user?.mfa?.isEnabled ? 'on' : 'off')} onPress={() => handleTabChange('2fa')} />
    </SettingsCardGroup>

    <SettingsCardGroup title={copy('connections')}>
      <AccountRow first last title={t('settings.account.connectedAccounts')} subtitle={copy('connectionsHelp')}
        icon="link" onPress={() => handleTabChange('connected')} />
    </SettingsCardGroup>
    <Text size="s" color="secondary" style={styles.footer}>{copy('footer')}</Text>
  </View>;
}

const styles = StyleSheet.create({
  identity: { padding: 24, flexDirection: 'row', alignItems: 'center', gap: 20 },
  avatar: { width: 72, height: 72, borderRadius: 36, alignItems: 'center', justifyContent: 'center', borderWidth: 1, overflow: 'hidden' },
  avatarImage: { width: 72, height: 72 },
  identityCopy: { flex: 1, minWidth: 0, gap: 6 },
  email: { lineHeight: 21, flexShrink: 1 },
  editProfile: { alignSelf: 'flex-start', marginTop: 6 },
  row: { flexDirection: 'row', alignItems: 'center', gap: 16, padding: 20, minHeight: 88 },
  rowIcon: { width: 38, height: 38, borderRadius: 10, alignItems: 'center', justifyContent: 'center' },
  rowCopy: { flex: 1, minWidth: 0, gap: 5 },
  rowTitle: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 8 },
  status: { paddingHorizontal: 8, paddingVertical: 3, borderRadius: 6, overflow: 'hidden' },
  description: { lineHeight: 21 },
  detail: { maxWidth: 720, width: '100%', alignSelf: 'center' },
  back: { alignSelf: 'flex-start', marginBottom: 14 },
  detailHeading: { gap: 8, marginBottom: 24 },
  formCard: { padding: 24 },
  footer: { lineHeight: 21, paddingHorizontal: 4 },
});

export default React.memo(AccountSettings);
